import type { ReactNode } from 'react'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from './alert-dialog'
import { buttonVariants } from './button'
import { cn } from '../../lib/utils'

export interface ConfirmDialogProps {
	open:         boolean
	onOpenChange: (open: boolean) => void
	title:        ReactNode
	description?: ReactNode
	confirmLabel: ReactNode
	cancelLabel:  ReactNode
	onConfirm:    () => void | Promise<void>
	/** Style of the confirm button. `destructive` paints it red. */
	variant?:     'default' | 'destructive'
	loading?:     boolean
	/** Extra content rendered between the header and the action buttons. */
	children?:    ReactNode
}

/**
 * Standardised confirm flow on top of `AlertDialog`. Replaces ad-hoc
 * confirmation scaffolding sprinkled across pages.
 */
function ConfirmDialog({
	open, onOpenChange,
	title, description,
	confirmLabel, cancelLabel,
	onConfirm,
	variant = 'default',
	loading = false,
	children,
}: ConfirmDialogProps) {
	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{title}</AlertDialogTitle>
					{description && <AlertDialogDescription>{description}</AlertDialogDescription>}
				</AlertDialogHeader>
				{children}
				<AlertDialogFooter>
					<AlertDialogCancel disabled={loading}>{cancelLabel}</AlertDialogCancel>
					<AlertDialogAction
						className={cn(variant === 'destructive' && buttonVariants({ variant: 'destructive' }))}
						disabled={loading}
						onClick={() => void onConfirm()}
					>
						{confirmLabel}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

export { ConfirmDialog }
